import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import { useCountUp } from '@/lib/useCountUp'
import { cn } from '@/lib/utils'
import { Skeleton } from './Skeleton'

interface StatCardProps {
  label: string
  /** Uitleg bij het label, getoond via een tooltip op hover/tap. */
  definition?: string
  value?: string
  numericValue?: number
  formatValue?: (value: number) => string
  delta?: string
  deltaTone?: 'up' | 'down' | 'neutral'
  icon?: LucideIcon
  chart?: ReactNode
  loading?: boolean
}

function AnimatedValue({ target, format }: { target: number; format: (value: number) => string }) {
  const value = useCountUp(target)
  return <>{format(value)}</>
}

export function StatCard({ label, definition, value, numericValue, formatValue, delta, deltaTone = 'neutral', icon: Icon, chart, loading }: StatCardProps) {
  const animated = numericValue !== undefined && formatValue !== undefined
  return (
    <div className="rounded-card bg-surface p-5 shadow-card">
      <div className="flex items-center justify-between">
        <Tooltip content={definition}>
          <span className="text-sm text-ink-faint">{label}</span>
        </Tooltip>
        {Icon ? <Icon className="h-4 w-4 text-ink-faint" strokeWidth={1.85} /> : null}
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          {loading || (value === undefined && !animated) ? (
            <Skeleton className="h-8 w-28" />
          ) : (
            <span className="money text-[25px] font-semibold leading-none tracking-[-0.02em] text-ink">
              {animated ? <AnimatedValue target={numericValue} format={formatValue} /> : value}
            </span>
          )}
          {delta ? (
            <span
              className={cn(
                'mt-2 block text-xs font-medium',
                deltaTone === 'up' && 'text-status-paid',
                deltaTone === 'down' && 'text-status-overdue',
                deltaTone === 'neutral' && 'text-ink-faint',
              )}
            >
              {delta}
            </span>
          ) : null}
        </div>
        {chart && !loading ? <div className="shrink-0 opacity-80">{chart}</div> : null}
      </div>
    </div>
  )
}
